import React, { useState } from 'react';
import { Button, Form, message, Modal } from 'antd';
import { CloseXIcon } from 'icons';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { PatternFormat } from 'react-number-format';

import { useAppDispatch, useAppSelector } from 'store';

import { useCheckSms, useSendSms } from 'modules/auth';
import { globalConst, setFinishLoginModal } from 'modules/global';

export const FinishLoginModal = () => {
  const t = useTranslations();
  const { replace } = useRouter();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const user = useAppSelector(state => state.user);
  const open = useAppSelector(({ confirmationModal }) => confirmationModal.finishLoginModalOpen);
  const ticketId = useAppSelector(({ authModal }) => authModal.ticketId);
  const isRouteLoading = useAppSelector(({ routeLoading }) => routeLoading.isRouteLoading);
  const [step, setStep] = useState<1 | 2 | 3>(1);

  const { mutate: sendSms, isLoading: sendingSms } = useSendSms();
  const { mutate: checkSms, isLoading: checkingSms } = useCheckSms();

  const onFinish = async (values: { phone: string; code: string }) => {
    const { code } = values;
    const phone = values.phone.replace(/\s|-/g, '');
    const isPhoneValid = phone?.length === 13;

    if (!isPhoneValid) {
      return form.setFields([
        {
          name: 'phone',
          errors: [t('Togri_telefon_raqam_kiriting')]
        }
      ]);
    }

    if (step === 1) {
      return sendSms(phone, {
        onSuccess: () => {
          setStep(2);
        }
      });
    }

    if (step === 2) {
      return checkSms(
        { phone, code, isSimpleLogin: true },
        {
          onSuccess: async () => {
            message.success(t('royxatdan_muvaffaqiyatli_otdingiz'));
            setStep(3);
          }
        }
      );
    }

    if (step === 3) {
      await replace(`${globalConst.pageNames.RESULTS}/${ticketId}`);
      if (!isRouteLoading) {
        dispatch(setFinishLoginModal(false));
      }
      form.resetFields();
      setStep(1);
    }

    return undefined;
  };

  return (
    <Modal
      footer={false}
      open={open}
      centered
      onCancel={() => {
        dispatch(setFinishLoginModal(false));
        setStep(1);
        form.resetFields();
      }}
      width="400px"
      closeIcon={
        <div className="flex items-center justify-center rounded-full bg-gray-800 p-1 text-white">
          <CloseXIcon size="12" />
        </div>
      }
      styles={{
        content: { padding: '16px', borderRadius: '12px' }
      }}
      title={<span className="text-lg">{t('home.banner.royxatdan_otish')}</span>}
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{
          user_name: user?.name || user?.last_name ? `${user?.name ?? ''} ${user?.last_name ?? ''}`.trim() : undefined,
          phone: user?.phone || '+998'
        }}
        onFinish={onFinish}
      >
        <Form.Item name="phone" label={t('auth.phone_placeholder')} rules={[{ required: true }]}>
          <PatternFormat
            format="+### ## ### ## ##"
            allowEmptyFormatting
            mask=" "
            type="tel"
            disabled={step === 2}
            placeholder={t('auth.phone_placeholder')}
            className={`!h-13 !rounded-xl !border !p-3 hover:!border-green-500 ${
              form.getFieldError('phone')?.length ? '!border-red-500' : '!border-transparent'
            } w-full !bg-gray-100 hover:!outline-green-500 focus:!outline-green-500`}
          />
        </Form.Item>

        {step !== 1 && (
          <div className="flex items-center gap-1">
            <Form.Item
              name="code"
              label={t('auth.otp_validation_message')}
              className="!flex-1"
              rules={[
                { required: true, message: t('auth.otp_validation_message') },
                { len: 5, message: t('auth.otp_validation_message') }
              ]}
            >
              <PatternFormat
                format="#####"
                mask="_"
                allowEmptyFormatting
                disabled={step === 3}
                placeholder="_ _ _ _ _"
                autoFocus
                className="!h-13 w-full flex-1 !rounded-xl !border-transparent !bg-gray-100 !p-3 hover:!outline-green-500 focus:!outline-green-500"
              />
            </Form.Item>
            <Button
              htmlType="submit"
              loading={checkingSms}
              className="!h-13 !mt-3 mb-1.5 !rounded-xl !border-none !bg-green-500 !px-4 !text-white hover:!border-none"
            >
              {t('auth.otp_title')}
            </Button>
          </div>
        )}

        <Button
          htmlType="submit"
          loading={sendingSms || checkingSms || isRouteLoading}
          disabled={step === 2}
          className="!mt-3 !h-11 !w-full !rounded-lg !border-none !bg-green-500 !px-7 !py-5 !text-white hover:!border-none disabled:!bg-gray-200"
        >
          {step === 1 ? t('tasdiqlash_kodini_olish') : t('natijani_korish')}
        </Button>
      </Form>
    </Modal>
  );
};
